//done
//@ts-check
import * as config from "../constants.js";
import { debug, isDebug, CH } from './debugLog.js';

const allItems = [
    ...(config.itemsCommon || []),
    ...(config.itemsUncommon || []),
    ...(config.itemsRare || []),
    ...(config.itemsBlueprints || []),
    ...(config.itemsCommonConsumables || []),
    ...(config.itemsUncommonConsumables || []),
    ...(config.itemsRareConsumables || []),
    ...(config.itemsEpicConsumableDominion || []),
    ...(config.itemsEpicConsumableWilders || []),
    ...(config.itemsEpicConsumableVeil || []),
    ...(config.itemsEpicDeviceDominion || []),
    ...(config.itemsEpicDeviceWilders || []),
    ...(config.itemsEpicDeviceVeil || []),
    ...(config.itemsStoryRelics || []),
    ...(config.itemsVendorRelicsCommon || []),
    ...(config.itemsVendorRelicsUncommon || []),
    ...(config.itemsVendorRelicsRare || []),
    ...(config.itemsVendorRelicsEpic || [])
];

// Shuffle array utility function. `rand` is injectable so a caller that needs
// a reproducible result — a test pinning a generated layout, say — can hand in
// a seeded stream instead of Math.random.
export function shuffleArray(array, rand = Math.random) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

// Get a random avatar class from the sprite sheets
export function getRandomAvatar(faction = null) {
    debug(CH.events, "Selecting random avatar for faction:", faction);
    const factions = ['dominion', 'veil', 'wilders'];
    const factionKey = faction || factions[Math.floor(Math.random() * factions.length)];
    const position = Math.floor(Math.random() * 32); // 0-31 for 32 avatars per faction
    return `avatar-${factionKey}-${position}`;
}

// Stable seed string for a procedural avatar (js/modules/avatarGen.js). Every
// character/contractor gets an explicit `avatarSeed` at creation, so this is a
// defensive fallback for anything that slipped through: prefer the explicit
// seed, then a stable id, then the name, then the legacy sprite string. NOTE a
// contractor's `id` is a reused negative slot index, so it must not be relied on
// here — contractors always carry an explicit `avatarSeed`.
export function getAvatarSeed(entity) {
    if (!entity) return "seed";
    return entity.avatarSeed || entity.id || entity.name || entity.avatar || "seed";
}

// Build the data-* attributes for a procedural-avatar placeholder div. A custom
// avatar (an explicit feature object from the creator) is emitted as
// data-avatar-features and takes precedence during hydration; the seed is always
// included as a fallback. Returns a raw attribute string for template injection.
export function avatarDataAttrs(entity) {
    const faction = (entity && entity.faction) || "dominion";
    let attrs = `data-avatar-seed="${getAvatarSeed(entity)}" data-avatar-faction="${faction}"`;
    if (entity && entity.avatarFeatures) {
        const json = JSON.stringify(entity.avatarFeatures)
            .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/'/g, "&#39;");
        attrs += ` data-avatar-features='${json}'`;
    }
    return attrs;
}

export function getSkillDisplayName(skillKey) {
    // Find the skill in faction data
    for (const factionKey of Object.keys(config.factionSkills)) {
        const faction = config.factionSkills[factionKey];
        if (faction.skills[skillKey]) {
            return faction.skills[skillKey].name;
        }
    }
    return skillKey; // Fallback to raw key
}

export function getSkillIcon(skillKey) {
    // Find the skill in faction data
    for (const factionKey of Object.keys(config.factionSkills)) {
        const faction = config.factionSkills[factionKey];
        if (faction.skills[skillKey]) {
            return faction.skills[skillKey].icon || '❓';
        }
    }
    return '❓'; // Fallback icon
}

export function getSkillNames(skillIds) {
    // Get display names for an array of skill IDs
    return skillIds.map(skillId => getSkillDisplayName(skillId));
}

// skillId -> faction. Memoized: factionSkills is static config, and this is
// called per-tile and per-room in hot paths.
let skillFactionMap = null;

/**
 * Which faction a skill belongs to, or null for an unrecognised skill.
 *
 * Lives here because three places needed it independently — gear sourcing, raid
 * loot attribution, and L3 demand pricing — and each had grown its own copy.
 */
export function getSkillFaction(skillId) {
    if (!skillFactionMap) {
        skillFactionMap = {};
        Object.entries(config.factionSkills).forEach(([faction, def]) => {
            Object.keys(def.skills || {}).forEach(s => { skillFactionMap[s] = faction; });
        });
    }
    return skillFactionMap[skillId] || null;
}

export function getEffect(effectId) {
  return config.itemEffects.find(effect => effect && effect.id === effectId);
}

export function getTechniqueName(techniqueId) {
    // Find the technique in skill techniques data
    for (const faction of Object.values(config.skillTechniques)) {
        const technique = faction.find(t => t.id === techniqueId);
        if (technique) {
            return technique.name;
        }
    }
    return techniqueId; // Fallback to ID if not found
}

export function getTechniqueIcon(techniqueId) {
    // Find the technique in skill techniques data
    for (const faction of Object.values(config.skillTechniques)) {
        const technique = faction.find(t => t.id === techniqueId);
        if (technique) {
            return technique.icon;
        }
    }
    return techniqueId; // Fallback to ID if not found
}

export function getTechniqueSkill(techniqueId) {
    // Find the technique in skill techniques data
    for (const skillTechnique of Object.values(config.skillTechniques)) {
        const technique = skillTechnique.find(t => t.id === techniqueId);
        if (technique) {
            return technique.skill;
        }
    }
    return null; //null if not found 
}

export function getItemName(itemId) {
    // Find item by ID and return its name
    const item = findItem('id', itemId);
    return item ? item.name : itemId; // Fallback to ID if not found
}

export function getRoomIcon(roomName, roomType) {
    if (roomType == 'start') return '🌀';
    else if (roomType == 'exit') return '🌌';
    else if (roomType == 'road') return '🏃‍♀️‍➡️';
    else if (roomType == 'blocked') return '⛔';
    else if (roomType == 'campsite') return '🏕️';
    else if (roomType == 'water') return '🌊';
    else {
        for(const region of Object.values(config.raidRegions)) {
            for(const room of region.gates) {
                if(room.name === roomName) {
                    return room.icon || '❓';
                }
            }
            for(const room of region.hazards) {
                if(room.name === roomName) {
                    return room.icon || '❓';
                }
            }
        }
    }
    return '❓'; // Fallback icon
}

export function getRoomDuration(room) {
    // Find the room in region data
    for (const regionKey of Object.keys(config.raidRegions)) {
        const region = config.raidRegions[regionKey];
        for (const roomData of [...region.gates, ...region.hazards]) {
            if (roomData.name === room) {
                return roomData.duration || 60; // Default to 1 minutes if not specified
            }
        }
    }
    return 30; // Fallback to 30 seconds
}

/**
 * Compact number for width-constrained UI (the header currency chips).
 *
 * Exists to stop the credits chip growing with wealth. It was 84px at `1000`
 * and 132px at `999,999,999`, so the header got closer to clipping the longer
 * someone played - an overflow driver no amount of label-trimming fixes,
 * because it scales with the save rather than the layout. This bounds the chip
 * at 5 characters permanently.
 *
 * Exact below 100,000 on purpose: that covers early and mid game, where credit
 * decisions are finest-grained and "25.1K" would hide whether you can afford a
 * 25,200 item. Above it, the exact digits stop being actionable at a glance and
 * the width matters more. Do NOT use this where a precise figure is the point
 * (promotion levy, raid payouts) - those use toLocaleString().
 *
 * @param {number} n
 * @returns {string} e.g. 25115 -> "25115", 250000 -> "250K", 1500000 -> "1.5M"
 */
export function formatCompact(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);

  if (abs < 100000) return sign + String(Math.floor(abs));

  const units = [
    { limit: 1e12, suffix: 'T' },
    { limit: 1e9, suffix: 'B' },
    { limit: 1e6, suffix: 'M' },
    { limit: 1e3, suffix: 'K' },
  ];
  for (const { limit, suffix } of units) {
    if (abs < limit) continue;
    const scaled = abs / limit;
    const rounded = scaled < 10 ? Math.round(scaled * 10) / 10 : Math.round(scaled);
    // Rounding can carry past the unit: 999,999 scales to 999.999K, which would
    // render "1000K" instead of "1M". Re-enter one unit up rather than printing
    // a value the next suffix already covers.
    if (rounded >= 1000) return formatCompact(limit * 1000);
    return sign + String(rounded) + suffix;
  }
  // Beyond the largest unit (>=1000T); no suffix left, so give the raw figure.
  return sign + String(Math.floor(abs));
}

export function debounce(func, delay) {
  let timeoutId; // This will store the ID of the setTimeout

  return function(...args) { // Returns a new export function that will be debounced
    const context = this; // Preserves the 'this' context of the original function

    clearTimeout(timeoutId); // Clear any existing timeout

    timeoutId = setTimeout(() => {
      func.apply(context, args); // Execute the original export function after the delay
    }, delay);
  };
}

export function showModal(title, content) {
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.innerHTML = `
        <div class="modal-content max-w-5xl">
            <div class="modal-header">
                <h3 class="modal-title">${title}</h3>
                <button class="close-btn">&times;</button>
            </div>
            <div class="modal-body">
                ${content}
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    const closeBtn = modal.querySelector('.close-btn');
    const closeModal = () => {
        //modal.classList.add('animate__animated', 'animate__fadeOut');
        setTimeout(() => {
            document.body.removeChild(modal);
        }, 150);
    };

    closeBtn.addEventListener('click', closeModal);

    let mousedownTarget = null;
    modal.addEventListener('mousedown', (e) => {
        mousedownTarget = e.target;
    });
    modal.addEventListener('click', (e) => {
        if (e.target === modal && mousedownTarget === modal) {
            closeModal();
        }
    });

    //modal.classList.add('animate__animated', 'animate__fadeIn');
    modal.querySelector('.modal-content').classList.add('animate__animated', 'animate__zoomIn');
}

// ========== ITEM LOOKUP HELPERS ==========
// Generic search functions for item/artifact arrays

export function findItem(property, value) {
  return allItems.find(item => item && item[property] === value);
}

/**
 * Batch lookup multiple items by ID in a single pass through allItems array
 * More performant than calling findItem multiple times when looking up many items
 * @param {Array<string>} itemIds - Array of item IDs to find
 * @returns {Object<string, object>} Map of itemId -> item object (undefined if not found)
 */
export function findItemsById(itemIds) {
  const result = {};
  itemIds.forEach(id => { result[id] = undefined; });

  for (const item of allItems) {
    if (!item) continue;
    if (result.hasOwnProperty(item.id) && result[item.id] === undefined) {
      result[item.id] = item;
    }
    if (result.length == itemIds.length)
      break;
  }

  return result;
}

export function findArtifact(property, value) {
  const allArtifacts = [
    ...(config.itemsArtifactsSkill || []),
    ...(config.itemsArtifactsUtility || [])
  ];
  return allArtifacts.find(artifact => artifact && artifact[property] === value);
}

export function getAllItems() {
  return allItems;
}

export function getAllArtifacts() {
  return [
    ...(config.itemsArtifactsSkill || []),
    ...(config.itemsArtifactsUtility || [])
  ];
}

// Basic markdown to HTML converter with navigation
export function convertMarkdownToHTML(markdown) {
    const mdH1Color = 'amber-400';
    const mdH2Color = 'cyan-300';
    const mdH3Color = 'emerald-400';
    const mdH4Color = 'gray-300';
    const lines = markdown.split('\n');
    const result = [];
    const sections = [];
    let inList = false;
    let sectionId = 0;

    for (let i = 0; i < lines.length; i++) {
        let line = lines[i];

        // Headers (check longest first)
        if (line.startsWith('#### ')) {
            if (inList) { result.push('</ul>'); inList = false; }
            result.push(`<h4 class="text-lg font-bold text-${mdH4Color} mt-4 mb-2">${line.slice(5)}</h4>`);
        } else if (line.startsWith('### ')) {
            if (inList) { result.push('</ul>'); inList = false; }
            result.push(`<h3 class="text-xl font-bold text-${mdH3Color} mt-6 mb-3">${line.slice(4)}</h3>`);
        } else if (line.startsWith('## ')) {
            if (inList) { result.push('</ul>'); inList = false; }
            const title = line.slice(3);
            const id = `section-${sectionId++}`;
            sections.push({ id, title });
            result.push(`<h2 id="${id}" class="text-2xl font-bold text-${mdH2Color} mt-8 mb-4 scroll-mt-36">${title}</h2>`);
        } else if (line.startsWith('# ')) {
            if (inList) { result.push('</ul>'); inList = false; }
            result.push(`<h1 class="text-3xl font-bold text-${mdH1Color} mb-6">${line.slice(2)}</h1>`);
        }
        // Horizontal rules
        else if (line === '---') {
            if (inList) { result.push('</ul>'); inList = false; }
            result.push('<hr class="border-gray-700 my-6">');
        }
        // Lists
        else if (line.startsWith('- ')) {
            if (!inList) {
                result.push('<ul class="list-disc ml-6 space-y-1 mb-4">');
                inList = true;
            }
            let content = line.slice(2);
            content = content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-white">$1</strong>');
            content = content.replace(/`([^`]+)`/g, '<code class="bg-gray-800 px-2 py-1 rounded text-sm">$1</code>');
            result.push(`<li class="ml-4">${content}</li>`);
        }
        // Empty lines
        else if (line.trim() === '') {
            if (inList) { result.push('</ul>'); inList = false; }
            result.push('');
        }
        // Paragraphs
        else {
            if (inList) { result.push('</ul>'); inList = false; }
            let content = line;
            content = content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-white">$1</strong>');
            content = content.replace(/`([^`]+)`/g, '<code class="bg-gray-800 px-2 py-1 rounded text-sm">$1</code>');
            result.push(`<p class="mb-3 text-gray-300">${content}</p>`);
        }
    }

    // Close any open list
    if (inList) result.push('</ul>');

    // Build navigation
    const nav = sections.map(s =>
        `<button onclick="event.stopPropagation(); document.getElementById('${s.id}').scrollIntoView({behavior:'smooth',block:'start'})" class="px-3 py-1 text-sm bg-game-accent/25 hover:bg-game-accent/50 hover:text-white text-${mdH2Color} rounded transition-colors whitespace-nowrap">${s.title}</button>`
    ).join('');

    const navBar = `<div class="sticky top-0 z-10 bg-game-bg border-b border-gray-700 p-3 mb-4 flex gap-2 flex-wrap">${nav}</div>`;

    return navBar + '<div class="p-4">' + result.join('\n') + '</div>';
}